// decimal-restriction.directive.ts
import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appDecimalRestriction]'
})
export class DecimalRestrictionDirective {
  @Input() maxValue: number = 999999999;
  @Input() decimalPlaces: number = 2;

  private specialKeys: string[] = [
    'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete', 'Clear'
  ];

  private isProcessing = false;

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Allow navigation/editing keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }

    // Allow Ctrl/Meta combos for common shortcuts
    if ((event.ctrlKey || (event as any).metaKey) && ['a', 'c', 'v', 'x', 'z'].indexOf((event.key || '').toLowerCase()) !== -1) {
      return;
    }

    const native: HTMLInputElement = this.el.nativeElement;
    const current: string = native.value || '';
    const pos = native.selectionStart || 0;

    // Only allow digits and dot
    if (!/[\d.]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Prevent multiple dots
    if (event.key === '.' && current.indexOf('.') !== -1) {
      event.preventDefault();
      return;
    }

    // Prevent typing beyond decimal places
    if (current.indexOf('.') !== -1) {
      const idxDot = current.indexOf('.');
      const decimalPart = current.substring(idxDot + 1);
      // If cursor is after dot and decimal length reached
      if (pos > idxDot && decimalPart.length >= this.decimalPlaces) {
        event.preventDefault();
        return;
      }
    }

    // Predict next numeric value (best-effort)
    const next = current.slice(0, pos) + event.key + current.slice(pos);
    const parsed = parseFloat(next);
    if (!isNaN(parsed) && parsed > this.maxValue) {
      event.preventDefault();
      return;
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const input: HTMLInputElement = event.target;
    let val: string = input.value || '';
    const origLen = val.length;
    const cursorStart = input.selectionStart || 0;

    // remove anything except digits and dot
    val = val.replace(/[^0-9.]/g, '');

    // if more than one dot, keep first dot and join rest
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }

    // limit decimal places
    if (val.indexOf('.') !== -1) {
      const [intPart, decPart] = val.split('.');
      if (decPart && decPart.length > this.decimalPlaces) {
        val = intPart + '.' + decPart.substring(0, this.decimalPlaces);
      }
    }

    // remove leading zeros (but keep "0" or "0.xxx")
    if (val.length > 1 && val[0] === '0' && val[1] !== '.') {
      val = val.replace(/^0+/, '');
      if (val === '') val = '0';
    }

    // clamp to maxValue (preserve decimalPlaces when clamping)
    const numVal = parseFloat(val);
    if (!isNaN(numVal) && isFinite(this.maxValue) && numVal > this.maxValue) {
      val = (typeof this.decimalPlaces === 'number' && this.decimalPlaces >= 0)
        ? this.maxValue.toFixed(this.decimalPlaces)
        : String(this.maxValue);
    }

    // if changed, write back and restore caret reasonably
    if (input.value !== val) {
      input.value = val;

      // restore caret position (best-effort)
      const newLen = val.length;
      const diff = origLen - newLen;
      let newCursor = Math.max(0, Math.min(cursorStart - diff, newLen));
      // ensure caret position not inside removed characters
      setTimeout(() => {
        try {
          input.setSelectionRange(newCursor, newCursor);
        } catch (e) {
          input.focus();
        }
      }, 0);

      // Tell Angular about the change (works with Angular 2.3.1)
      this.dispatchInputEvent(input);
    }

    this.isProcessing = false;
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    let pasted = '';
    if (event.clipboardData) {
      pasted = event.clipboardData.getData('text');
    } else if ((window as any).clipboardData) {
      pasted = (window as any).clipboardData.getData('text');
    }

    // sanitize pasted
    let clean = (pasted || '').replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    if (parts.length > 2) {
      clean = parts[0] + '.' + parts.slice(1).join('');
    }

    if (clean.indexOf('.') !== -1) {
      const [intPart, decPart] = clean.split('.');
      if (decPart && decPart.length > this.decimalPlaces) {
        clean = intPart + '.' + decPart.substring(0, this.decimalPlaces);
      }
    }

    const num = parseFloat(clean);
    if (!isNaN(num) && isFinite(this.maxValue) && num > this.maxValue) {
      clean = (typeof this.decimalPlaces === 'number' && this.decimalPlaces >= 0)
        ? this.maxValue.toFixed(this.decimalPlaces)
        : String(this.maxValue);
    }

    // insert cleaned value at caret
    const el: HTMLInputElement = this.el.nativeElement;
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const newVal = el.value.slice(0, start) + clean + el.value.slice(end);
    el.value = newVal;
    // move caret after pasted content
    const caretPos = start + clean.length;
    try { el.setSelectionRange(caretPos, caretPos); } catch (e) { el.focus(); }

    // notify Angular
    this.dispatchInputEvent(el);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any) {
    const input: HTMLInputElement = event.target;
    let val = input.value;
    if (val && val !== '') {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        // format to fixed decimal places
        const formatted = (typeof this.decimalPlaces === 'number' && this.decimalPlaces >= 0)
          ? num.toFixed(this.decimalPlaces)
          : String(num);
        if (input.value !== formatted) {
          input.value = formatted;
          this.dispatchInputEvent(input);
        }
      } else {
        // Not a number -> clear
        input.value = '';
        this.dispatchInputEvent(input);
      }
    }
  }

  private dispatchInputEvent(element: HTMLInputElement) {
    // Angular 2.3.1 friendly event dispatch
    try {
      const ev = new Event('input', { bubbles: true });
      element.dispatchEvent(ev);
    } catch (e) {
      const ev = document.createEvent('Event');
      ev.initEvent('input', true, true);
      element.dispatchEvent(ev);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/decimal-restriction.directive.ts